// Todo operations for SyncedStore
import { v4 as uuidv4 } from 'uuid';
import { getDoc, getClientId } from './connection';
import type { Todo, WorkerOut, WorkerAssignment, WorkerAssignmentLog } from './types';

// This will be set by the main store index
let store: any = null;

export function setStore(storeInstance: any) {
  store = storeInstance;
}

export function addTodo(title: string) {
  const doc = getDoc();
  if (!doc || !store) return;

  // Validate required fields
  if (!title.trim()) {
    throw new Error('Todo title is required');
  }

  const newTodo: Todo = {
    id: uuidv4(),
    title: title.trim(),
    completed: false,
    timestamp: Date.now(),
    clientId: getClientId()
  };

  doc.transact(() => {
    store.todos.push(newTodo);
  });
}

export function editTodo(id: string, newTitle: string) {
  const doc = getDoc();
  if (!doc || !store) return;
  
  // Validate title
  if (!newTitle.trim()) {
    throw new Error('Todo title cannot be empty');
  }
  
  doc.transact(() => {
    const todo = store.todos.find((t: Todo) => t.id === id);
    if (todo) {
      todo.title = newTitle.trim();
      todo.timestamp = Date.now();
      todo.clientId = getClientId();
    }
  });
}

export function toggleTodo(id: string) {
  const doc = getDoc();
  if (!doc || !store) return;
  
  doc.transact(() => {
    const todo = store.todos.find((t: Todo) => t.id === id);
    if (todo) {
      todo.completed = !todo.completed;
      todo.timestamp = Date.now();
      todo.clientId = getClientId();
    }
  });
}

export function deleteTodo(id: string) {
  const doc = getDoc();
  if (!doc || !store) return;
  
  doc.transact(() => {
    const index = store.todos.findIndex((t: Todo) => t.id === id);
    if (index !== -1) {
      store.todos.splice(index, 1);
    }
  });
}

export function addWorkerOut(teamId: string, workerId: string) {
  const doc = getDoc();
  if (!doc || !store) return;

  // Validate required fields
  if (!teamId || !workerId) {
    throw new Error('Team id and worker id are required');
  }

  const newWorkerOut: WorkerOut = {
    id: uuidv4(),
    workerId,
    teamId,
    timestamp: Date.now(),
    clientId: getClientId()?.toString()!
  };

  doc.transact(() => {
    store.workerOuts.push(newWorkerOut);
  });
}

export function revertWorkerOut(workerOutId: string) {
  const doc = getDoc();
  if (!doc || !store) return;

  // Validate required fields
  if (!workerOutId) {
    throw new Error('Worker out id is required');
  }

  doc.transact(() => {
    const workerOut = store.workerOuts.find((w: WorkerOut) => w.id === workerOutId);
    if (workerOut) {
      workerOut.softDeleted = true;
      workerOut.timestamp = Date.now();
      workerOut.clientId = getClientId()?.toString()!;
    }
  });
}

export function addWorkerAssignment(teamId: string, workerId: string, taskTypeId: string, zoneId: string, locationId?: string) {
  const doc = getDoc();
  if (!doc || !store) return;

  // Validate required fields
  if (!teamId || !workerId || !taskTypeId || !zoneId) {
    throw new Error('Team id, worker id, task type id, and zone id are required');
  }

  const newAssignment: WorkerAssignment = {
    id: uuidv4(),
    workerId,
    teamId,
    taskTypeId,
    zoneId,
    locationId,
    timestamp: Date.now(),
    clientId: getClientId()?.toString()!
  };

  doc.transact(() => {
    store.workerAssignments.push(newAssignment);
  });
}

export function unassignWorker(assignmentId: string) {
  const doc = getDoc();
  if (!doc || !store) return;

  // Validate required fields
  if (!assignmentId) {
    throw new Error('Assignment id is required');
  }

  doc.transact(() => {
    const assignment = store.workerAssignments.find((a: WorkerAssignment) => a.id === assignmentId);
    if (assignment) {
      assignment.softDeleted = true;
      assignment.timestamp = Date.now();
      assignment.clientId = getClientId()?.toString()!;
    }
  });
}

export function softDeleteWorkerAssignments(workerId: string) {
  const doc = getDoc();
  if (!doc || !store) return;

  // Validate required fields
  if (!workerId) {
    throw new Error('Worker id is required');
  }

  doc.transact(() => {
    const assignments = store.workerAssignments.filter((a: WorkerAssignment) => 
      a.workerId === workerId && !a.softDeleted
    );
    
    assignments.forEach((assignment: WorkerAssignment) => {
      assignment.softDeleted = true;
      assignment.timestamp = Date.now();
      assignment.clientId = getClientId()?.toString()!;
    });
  });
}

export function getWorkerAssignments(workerId: string) {
  if (!store) return [];

  return store.workerAssignments.filter((a: WorkerAssignment) => 
    a.workerId === workerId && !a.softDeleted
  );
}

export function addWorkerAssignmentLog(
  teamId: string,
  workerId: string,
  taskTypeId: string,
  zoneId: string,
  value: number,
  locationId?: string
) {
  const doc = getDoc();
  if (!doc || !store) return;

  // Validate required fields
  if (!teamId || !workerId || !taskTypeId || !zoneId || value === undefined || value === null) {
    throw new Error('Team id, worker id, task type id, zone id, and value are required');
  }

  const newLog: WorkerAssignmentLog = {
    id: uuidv4(),
    workerId,
    teamId,
    taskTypeId,
    zoneId,
    locationId,
    value,
    timestamp: Date.now(),
    clientId: getClientId()?.toString() || null
  };

  doc.transact(() => {
    store.workerAssignmentLogs.push(newLog);
  });
}

export function softDeleteWorkerAssignmentLog(logId: string) {
  const doc = getDoc();
  if (!doc || !store) return;

  // Validate required fields
  if (!logId) {
    throw new Error('Log id is required');
  }

  doc.transact(() => {
    const log = store.workerAssignmentLogs.find((l: WorkerAssignmentLog) => l.id === logId);
    if (log) {
      log.softDeleted = true;
      log.softDeletedTime = Date.now();
      log.clientId = getClientId()?.toString() || null;
    }
  });
}

export function getWorkerAssignmentLogs(workerId?: string, taskTypeId?: string) {
  if (!store) return [];

  return store.workerAssignmentLogs.filter((l: WorkerAssignmentLog) => {
    const matchesWorker = !workerId || l.workerId === workerId;
    const matchesTask = !taskTypeId || l.taskTypeId === taskTypeId;
    return matchesWorker && matchesTask;
  });
}

